import type { FC } from 'react';
import clsx from 'clsx';
import { EmptyState } from '@/components/shared/EmptyState';
import { ToolStateBadge } from '@/components/shared/ToolStateBadge';
import { Card } from '@/components/ui/card/Card';
import { useTranslation } from '@/hooks/useTranslation';
import { formatNumber } from '../lib/wearFormat';
import type { WearOutput } from '../type/WearTypes';

/** One row per trace with what it added to the wear; unusable traces listed, not counted. */
export const WearHistoryCard: FC<{ output: WearOutput }> = ({ output }) => {
  const { t, language } = useTranslation();
  const num = (value: number | null, digits: number) =>
    value === null ? '—' : formatNumber(language, value, digits);
  // What the trace did to the wear first; the settings that explain it after (they scroll).
  const columns = [
    'date',
    'operation',
    'pieces',
    'volume',
    'wearAdded',
    'cumulative',
    'state',
    'material',
    'ap',
    'ae',
    'fz',
    'hm',
    'machine',
  ];
  const numeric = new Set(['pieces', 'volume', 'wearAdded', 'cumulative', 'ap', 'ae', 'fz', 'hm']);

  const metrics = output.model.metrics ?? {};
  const fitted = output.model.fittedRange ?? {};
  const materials = Array.isArray(fitted.materials)
    ? (fitted.materials as string[]).join(', ')
    : null;
  const diameters = Array.isArray(fitted.diameter_mm) ? (fitted.diameter_mm as number[]) : null;
  const provenance = [
    t('mlWear.result.provenance', {
      name: output.model.name,
      version: output.model.version ? `v${output.model.version}` : '',
    }).replace(/\s+/g, ' '),
    typeof metrics.n_tools === 'number' && materials && diameters
      ? t('mlWear.result.provenanceFitted', {
          tools: metrics.n_tools,
          materials,
          diameter: formatNumber(language, diameters[0], 0),
        })
      : null,
    typeof metrics.rul_error_min === 'number'
      ? t('mlWear.result.provenanceError', {
          error: formatNumber(language, metrics.rul_error_min, 1),
        })
      : null,
  ].filter(Boolean);

  return (
    <Card as="section" className="flex min-w-0 flex-col gap-4" data-testid="wear-history">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">{t('mlWear.result.historyTitle')}</h2>
        <span className="text-xs text-muted-foreground">{t('mlWear.result.historyHelp')}</span>
      </div>
      {output.traces.length === 0 ? (
        <EmptyState size="compact" title={t('mlWear.result.historyEmpty')} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap text-sm">
            <thead>
              <tr className="eyebrow border-b border-border text-left">
                {columns.map((column) => (
                  <th
                    key={column}
                    className={clsx(
                      'whitespace-nowrap px-2 py-1.5 font-semibold',
                      numeric.has(column) && 'text-right'
                    )}
                  >
                    {t(`mlWear.result.columns.${column}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {output.traces.map((trace, index) => {
                const skipped = trace.skippedReason !== null;
                // In a trace left out of the wear, an empty field the model needs reads "missing".
                const value = (field: number | null, digits: number) =>
                  skipped && field === null ? (
                    <span className="font-semibold text-warning" data-testid="wear-trace-missing">
                      {t('mlWear.result.missingCell')}
                    </span>
                  ) : (
                    num(field, digits)
                  );
                return (
                  <tr
                    key={trace.traceId ?? index}
                    className={clsx(
                      'border-b border-border/50',
                      skipped && 'text-muted-foreground'
                    )}
                    data-testid={skipped ? 'wear-trace-skipped' : 'wear-trace'}
                  >
                    <td className="whitespace-nowrap px-2 py-1.5">
                      {trace.date ? new Date(trace.date).toLocaleDateString(language) : '—'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5">{trace.operation ?? '—'}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{num(trace.pieces, 0)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {value(trace.volume, 2)}
                    </td>
                    {skipped ? (
                      <td colSpan={2} className="px-2 py-1.5 text-right italic">
                        {t('mlWear.result.skipped', {
                          reason: t(`mlWear.result.skippedReasons.${trace.skippedReason}`),
                        })}
                      </td>
                    ) : (
                      <>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          +{num(trace.wearAdded === null ? null : trace.wearAdded * 100, 1)} %
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {num(
                            trace.wearCumulative === null ? null : trace.wearCumulative * 100,
                            0
                          )}{' '}
                          %
                        </td>
                      </>
                    )}
                    <td className="whitespace-nowrap px-2 py-1.5">
                      <ToolStateBadge toolState={trace.toolState} fallback="—" size="sm" />
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5">{trace.material ?? '—'}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{num(trace.ap, 1)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{value(trace.ae, 2)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{value(trace.fz, 3)}</td>
                    <td
                      className={clsx(
                        'px-2 py-1.5 text-right tabular-nums',
                        trace.outOfRange.includes('hm') && 'font-semibold text-warning'
                      )}
                    >
                      {num(trace.hm, 1)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5">{trace.machine ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Card
        as="p"
        tier="muted"
        padding="none"
        className="px-3 py-2 text-xs text-muted-foreground"
        data-testid="wear-provenance"
      >
        {provenance.join(' · ')}
      </Card>
    </Card>
  );
};
